import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Check, Plus, X } from "lucide-react";
import { LaneNote } from "./LaneNote";

const KINDS = ["Gallery", "Testimonials", "FAQ", "Contact", "Text", "Call to action"];

export const addSectionPicks = (picks: readonly string[], description: string): string[] => [...picks, ...(description.trim() ? [description.trim()] : [])];

interface AddSectionPickerProps {
  wide: boolean;
  after: string;
  collections: Array<{ id: string; label: string }>;
  top: number;
  left: number;
  onClose: () => void;
  onAdd: (picks: string[]) => void;
}

/** A requested-lane note: every selected idea becomes a separate pinned comment. */
export function AddSectionPicker({ wide, after, collections, top, left, onClose, onAdd }: AddSectionPickerProps) {
  const dialog = useRef<HTMLDivElement>(null);
  const [picks, setPicks] = useState<string[]>([]);
  const [description, setDescription] = useState("");
  const [shownTop, setShownTop] = useState(top);
  useEffect(() => { dialog.current?.querySelector<HTMLElement>("button")?.focus(); }, []);
  // Below the section when it fits; otherwise raised until it does, so it never opens off screen.
  useLayoutEffect(() => {
    const frame = dialog.current?.closest<HTMLElement>(".bw-frame");
    if (!wide || !dialog.current || !frame) return;
    setShownTop(Math.max(16, Math.min(top, frame.clientHeight - dialog.current.offsetHeight - 16)));
  }, [top, wide]);
  const toggle = (pick: string) => setPicks((current) => current.includes(pick) ? current.filter((item) => item !== pick) : [...current, pick]);
  const allPicks = addSectionPicks(picks, description);
  return <div className="bw-insert-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <div
      ref={dialog}
      className="bw-insert-picker"
      role="dialog"
      aria-modal={!wide}
      aria-labelledby="bw-insert-title"
      style={wide ? { top: shownTop, left } : undefined}
      onKeyDown={(event) => {
        if (event.key === "Escape") { event.stopPropagation(); onClose(); }
        if (event.key !== "Tab" || wide) return;
        const controls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>("button:not(:disabled), input:not(:disabled)"));
        const first = controls[0];
        const last = controls.at(-1);
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }}
    >
      <div className="bw-insert-head"><div><h2 id="bw-insert-title">Add a section</h2><p>After {after} · Stax builds it to match your site.</p></div><button type="button" className="bw-btn bw-btn--icon" onClick={onClose} aria-label="Close add section"><X size={18} aria-hidden="true" /></button></div>
      {collections.length > 0 && <section className="bw-insert-group" aria-labelledby="bw-insert-content"><h3 id="bw-insert-content">From your content</h3><div className="bw-insert-options">{collections.map((collection) => <Pick key={collection.id} label={collection.label} selected={picks.includes(collection.label)} onClick={() => toggle(collection.label)} />)}</div></section>}
      <section className="bw-insert-group" aria-labelledby="bw-insert-kinds"><h3 id="bw-insert-kinds">Kinds of section</h3><div className="bw-insert-options bw-insert-kinds">{KINDS.map((kind) => <Pick key={kind} label={kind} selected={picks.includes(kind)} onClick={() => toggle(kind)} />)}</div></section>
      <label className="bw-insert-description"><span>Or describe it</span><input value={description} onChange={(event) => setDescription(event.target.value)} placeholder="A testimonial from a student, with their photo" /></label>
      <div className="bw-insert-actions"><button type="button" className="bw-btn bw-btn--primary" disabled={allPicks.length === 0} onClick={() => { onAdd(allPicks); onClose(); }}><Plus size={16} aria-hidden="true" /> Add to batch</button></div>
      <LaneNote lane="requested">Your picks are pinned here as comments. Send them with your other comments, then review the new sections before they’re kept.</LaneNote>
    </div>
  </div>;
}

function Pick({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return <button type="button" className="bw-insert-pick" aria-pressed={selected} onClick={onClick}>{selected && <Check size={15} aria-hidden="true" />}<span>{label}</span></button>;
}
